import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';
import { GalleryVideo } from './events/EventMedia';

/**
 * Full-screen viewer for a single photo, shown whole.
 *
 * This exists because the avatars it opens from are a compromise: a round 50px
 * frame cropped to the top of the image (see lib/avatar.js). That frame is the
 * right default for a contact row, but it is still a crop, and on a full-length
 * portrait the agent ends up a thumbnail-sized figure inside it. Tapping gives
 * you the photo as it was actually uploaded.
 *
 * Deliberately NOT built on FileViewer. That one fetches the file into a blob so
 * it can render PDFs and hand a real File to the Web Share API — but 926 of the
 * 933 agent photos on prod are still served from the legacy erp213 Azure
 * container, which sends no CORS headers. `fetch` on those rejects before any
 * response is readable, so FileViewer can only ever show its "still held on the
 * old system" fallback for them. A plain <img src> is not subject to CORS at
 * all, so it simply works. The same constraint rules out a Share/download button
 * here: producing the bytes would need the very fetch that is blocked.
 *
 * Takes the app's one full-screen sheet shape — rise from the bottom, chevron
 * back in a surface header — rather than the usual photo-viewer black, because
 * a good number of these are studio cut-outs: transparent PNGs of agents in dark
 * suits, which vanish against a dark stage.
 *
 * Also pages through several photos (an event's poster and HQ's gallery): pass
 * `photos` and the index to `start` on instead of `url`. The slides sit side by
 * side in a scroll-snap row — the same swipe as the listing hero — so a swipe
 * moves one photo, and the arrow keys do the same on a keyboard. Each slide
 * keeps its own loading/error state, so one bad photo does not blank the rest.
 *
 * An entry of `photos` may be `{ url, video: true }` (an event gallery video):
 * that slide is a player instead of an image. It pauses when swiped away, and
 * the one the viewer opened on starts playing by itself.
 *
 * An entry may also carry its own `title` (project materials: each photo is a
 * named file), which the header shows while that slide is on screen; and
 * `actions(item, index)` renders buttons at the end of the header for the slide
 * on screen (Share / Download there). Both are optional — callers that pass
 * plain URLs see no change.
 */
export default function PhotoViewer({ open, url, photos, start = 0, title, alt, onClose, actions }) {
  const { mounted, closing } = useSheetAnim(open);
  const list = (photos?.length ? photos : url ? [url] : []).map((p) => (typeof p === 'string' ? { url: p } : p));
  const many = list.length > 1;
  const rowRef = useRef(null);
  const [index, setIndex] = useState(start);

  // Land on the photo that was tapped. Runs when the sheet mounts, not on every
  // `start` change: the caller clears its index as it closes, and the row must
  // not jump back to the poster during the exit animation.
  useLayoutEffect(() => {
    if (!mounted) return;
    const el = rowRef.current;
    const i = Math.min(Math.max(0, start), Math.max(0, list.length - 1));
    if (el) el.scrollLeft = i * el.clientWidth;
    setIndex(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      const el = rowRef.current;
      if (!el || !many) return;
      if (e.key === 'ArrowRight') el.scrollBy({ left: el.clientWidth, behavior: 'smooth' });
      if (e.key === 'ArrowLeft') el.scrollBy({ left: -el.clientWidth, behavior: 'smooth' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, many]);

  // Which slide the row has settled on, read off scrollLeft (as the listing hero
  // does) so a fling across two photos still counts right.
  const onRowScroll = (e) => {
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    setIndex((prev) => (prev === i ? prev : i));
  };

  if (!mounted || !list.length) return null;

  return (
    <div
      className={`sheet-panel${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex: 4700, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 15px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div onClick={onClose} role="button" aria-label="Close" style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 5 8 12 15 19" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{list[index]?.title || title || 'Photo'}</div>
        {many && (
          <div aria-live="polite" style={{ flex: 'none', fontSize: 12.5, fontWeight: 600, color: 'var(--muted)', paddingRight: 4 }}>
            {Math.min(index + 1, list.length)} / {list.length}
          </div>
        )}
        {actions && list[index] && actions(list[index], index)}
      </div>

      <div
        ref={rowRef}
        className="scrx"
        onScroll={many ? onRowScroll : undefined}
        style={{ flex: 1, minHeight: 0, display: 'flex', overflowX: many ? 'auto' : 'hidden', overflowY: 'hidden', scrollSnapType: 'x mandatory' }}
      >
        {list.map((item, i) => (
          <Slide
            key={`${i}-${item.url}`}
            url={item.url}
            video={!!item.video}
            active={i === index}
            autoPlay={i === start}
            alt={alt ? (many ? `${alt} (${i + 1} of ${list.length})` : alt) : (title ? `${title} — profile photo` : 'Profile photo')}
            onClose={onClose}
          />
        ))}
      </div>
    </div>
  );
}

function Slide({ url, alt, video, active, autoPlay, onClose }) {
  const [status, setStatus] = useState('loading'); // loading · ready · error

  // Reset per photo, otherwise reopening the viewer on a different agent
  // inherits the previous one's resolved state and shows a stale frame.
  useEffect(() => { setStatus('loading'); }, [url]);

  // Tapping the stage closes. The photo itself does not, so a tap that lands
  // on the face is never read as "dismiss".
  return (
    <div
      onClick={onClose}
      style={{ flex: 'none', width: '100%', height: '100%', scrollSnapAlign: 'center', scrollSnapStop: 'always', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, paddingBottom: 'calc(16px + env(safe-area-inset-bottom))' }}
    >
      {status === 'error' ? (
        <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', maxWidth: 260 }}>
          {video ? 'This video could not be played on this device.' : 'This photo could not be loaded.'}
        </div>
      ) : video ? (
        <>
          {status === 'loading' && <Spin />}
          <GalleryVideo
            url={url}
            active={active}
            autoPlay={autoPlay}
            onReady={() => setStatus('ready')}
            onError={() => setStatus('error')}
            style={{ width: '100%', display: status === 'ready' ? 'block' : 'none' }}
          />
        </>
      ) : (
        <>
          {status === 'loading' && <Spin />}
          <img
            src={url}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            onLoad={() => setStatus('ready')}
            onError={() => setStatus('error')}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 12,
              // White backing rather than a theme token: a transparent cut-out
              // needs something opaque and light behind it in BOTH themes.
              background: '#fff',
              display: status === 'ready' ? 'block' : 'none',
            }}
          />
        </>
      )}
    </div>
  );
}
